import { useCallback, useEffect, useMemo, useState } from "react";
import { STORAGE_KEYS } from "../config/newsConfig";
import { readStoredJson, writeStoredJson } from "../utils/storage";
import {
  getSourceKey,
  normalizeSourceList,
  normalizeSourceName,
  sortSourceListAlphabetically,
} from "../utils/sourceModel";

export const useSources = ({
  availableSources = [],
  sourceCatalog = [],
} = {}) => {
  const [activeSourcesState, setActiveSources] = useState(() => {
    const storedSources = readStoredJson(STORAGE_KEYS.sources, null);

    return storedSources === null
      ? null
      : sortSourceListAlphabetically(storedSources);
  });
  const [sourceSearchTerm, setSourceSearchTerm] = useState("");
  const [sourceSelectionRevision, setSourceSelectionRevision] = useState(0);
  const [isSourcePanelOpen, setIsSourcePanelOpen] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | Canonical source lookup
  |--------------------------------------------------------------------------
  |
  | The map is built only from the small source catalog (roughly 43 entries),
  | not from news rows. Values are exact catalog names used for API requests;
  | keys are comparison-only aliases that ignore spaces and half-spaces.
  |
  */
  const availableByKey = useMemo(() => {
    return new Map(
      normalizeSourceList(availableSources).map((source) => [
        getSourceKey(source),
        source,
      ])
    );
  }, [availableSources]);

  /*
  | Resolve old localStorage values synchronously as soon as the catalog is
  | available. This prevents one stale request with "فیلم زی" before the state
  | synchronization effect stores the canonical "فیلم‌زی" value.
  */
  const activeSources = useMemo(() => {
    if (activeSourcesState === null) return null;

    return sortSourceListAlphabetically(
      activeSourcesState.map((source) => {
        return (
          availableByKey.get(getSourceKey(source)) ||
          normalizeSourceName(source)
        );
      })
    );
  }, [activeSourcesState, availableByKey]);

  /*
  | Initialize a genuinely new user only from the authoritative source catalog.
  |
  | `availableSources` is intentionally not used here because it also contains
  | source names discovered from the first news batch. That batch can arrive
  | before the source-catalog request and may contain only a subset of all
  | sources. Persisting that transient subset would make first-run selection
  | depend on request timing.
  */
  useEffect(() => {
    if (activeSourcesState === null && sourceCatalog.length > 0) {
      const initialSources = sortSourceListAlphabetically(sourceCatalog);

      // Persist the initial selection in the same task as the state update.
      // Mobile PWAs can be suspended immediately after user interaction, so
      // persistence must not depend only on a later React effect.
      writeStoredJson(STORAGE_KEYS.sources, initialSources);
      setActiveSources(initialSources);
    }
  }, [activeSourcesState, sourceCatalog]);

  useEffect(() => {
    if (
      activeSourcesState === null ||
      activeSources === null ||
      availableByKey.size === 0
    ) {
      return;
    }

    const currentSignature = activeSourcesState.join("|");
    const nextSignature = activeSources.join("|");

    if (currentSignature !== nextSignature) {
      // Keep canonicalized source names and localStorage in lock-step.
      writeStoredJson(STORAGE_KEYS.sources, activeSources);
      setActiveSources(activeSources);
    }
  }, [activeSources, activeSourcesState, availableByKey]);

  useEffect(() => {
    if (activeSources !== null) {
      writeStoredJson(STORAGE_KEYS.sources, activeSources);
    }
  }, [activeSources]);

  const handleToggleSource = useCallback(
    (source) => {
      const sourceKey = getSourceKey(source);

      if (!sourceKey) return;

      const canonicalSource =
        availableByKey.get(sourceKey) || normalizeSourceName(source);

      setActiveSources((previousSources) => {
        const current = sortSourceListAlphabetically(previousSources || []);
        const alreadySelected = current.some((item) => {
          return getSourceKey(item) === sourceKey;
        });

        const nextSources = alreadySelected
          ? sortSourceListAlphabetically(
              current.filter((item) => {
                return getSourceKey(item) !== sourceKey;
              })
            )
          : sortSourceListAlphabetically([
              ...current,
              canonicalSource,
            ]);

        // Persist synchronously with the user's toggle. This fixes the mobile
        // PWA/TWA case where the OS can suspend/kill the app before the later
        // useEffect has a chance to flush the new selection to localStorage.
        writeStoredJson(STORAGE_KEYS.sources, nextSources);

        return nextSources;
      });

      setSourceSelectionRevision((previous) => previous + 1);
    },
    [availableByKey]
  );

  const toggleSourcePanel = useCallback(() => {
    setIsSourcePanelOpen((prev) => !prev);
  }, []);

  const closeSourcePanel = useCallback(() => {
    setIsSourcePanelOpen(false);
  }, []);

  return {
    activeSources,
    setActiveSources,
    sourceSelectionRevision,
    sourceSearchTerm,
    setSourceSearchTerm,
    isSourcePanelOpen,
    setIsSourcePanelOpen,
    handleToggleSource,
    toggleSourcePanel,
    closeSourcePanel,
  };
};
